import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  ClientIntegrationType,
  GetClientIntegrationDocument,
  GetIntegrationsSummaryDocument,
  ListClientIntegrationsDocument,
  SecurityPipelineGithubConfigurationOptionsInput,
  useCreateClientIntegrationMutation,
  useDeleteClientIntegrationMutation,
  useGetClientIntegrationQuery,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { getError } from "~/lib/handle-error";
import { useEffect, useState } from "react";
import { useDialogs } from "@toolpad/core";
import { GithubSecurityPipelineAssignSpacesDialog } from "~/pages/integrations/security-pipeline/security-pipeline-github/SecurityPipelineGithubOrgPage/components/SecurityPipelineGithubAssignSpaces/GithubSecurityPipelineAssignSpacesDialog";
import { useAssignSpacesToRepos } from "~/pages/integrations/security-pipeline/security-pipeline-github/SecurityPipelineGithubOrgPage/components/SecurityPipelineGithubAssignSpaces/hooks/useAssignSpacesToRepos";
import { GithubSecurityPipelineFormInput } from "~/pages/integrations/security-pipeline/security-pipeline-github/types";
import { SECURITY_PIPELINE_DISPLAY_NAME } from "~/pages/integrations/security-pipeline/constants";

export interface UseSetupParams {
  scope: SpaceScope | OrganizationScope;
  integrationId: string | null | undefined;
  canCreateIntegration: boolean;
}

const defaultValues: GithubSecurityPipelineFormInput = {
  integrationName: "",
  repoName: "mondoo-security-pipeline",
  assignedSpaces: [],
  generateSSHKey: false,
};

export const useSetup = ({
  scope,
  integrationId,
  canCreateIntegration,
}: UseSetupParams) => {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const integrationMrn = integrationId
    ? getIntegrationMrn({ scope, integrationId })
    : undefined;
  const defaultIntegrationName = "Security pipeline setup";
  const dialogs = useDialogs();
  const formMethods = useForm<GithubSecurityPipelineFormInput>({
    mode: "onBlur",
    defaultValues: {
      ...defaultValues,
      integrationName: defaultIntegrationName,
    },
  });
  const {
    reset,
    watch,
    formState: { isSubmitSuccessful },
  } = formMethods;

  const watchRepoName = watch("repoName") || "mondoo-security-pipeline";
  const watchAssignedSpaces = watch("assignedSpaces");

  const { data: clientIntegrationData, loading: clientIntegrationLoading } =
    useGetClientIntegrationQuery({
      notifyOnNetworkStatusChange: true,
      variables: { mrn: integrationMrn || "skip" },
      skip: !integrationMrn || !integrationId,
      // We cannot use cache here because we are using the same route for add and edit integration
      fetchPolicy: "network-only",
    });

  const integration = clientIntegrationData?.clientIntegration?.integration;

  const configurationOptions =
    clientIntegrationData?.clientIntegration?.integration?.configurationOptions
      ?.__typename === "SecurityPipelineGithubConfigurationOptions"
      ? clientIntegrationData?.clientIntegration?.integration
          ?.configurationOptions
      : undefined;

  const appInstallationLink = configurationOptions?.appInstallationLink;
  const repositories = configurationOptions?.repositories || [];
  const publicKey = configurationOptions?.publicKey;

  const [createIntegration, { loading: createIntegrationLoading }] =
    useCreateClientIntegrationMutation({
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      refetchQueries: [ListClientIntegrationsDocument],
    });

  const [updateIntegration, { loading: updateIntegrationLoading }] =
    useUpdateClientIntegrationConfigurationMutation({
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      refetchQueries: [
        ...(integrationId
          ? [
              {
                query: GetClientIntegrationDocument,
                variables: {
                  mrn: getIntegrationMrn({
                    scope,
                    integrationId: integrationId || "",
                  }),
                },
              },
            ]
          : []),
      ],
    });

  const [deleteIntegration, { loading: deleteIntegrationLoading }] =
    useDeleteClientIntegrationMutation({
      notifyOnNetworkStatusChange: true,
      refetchQueries: [
        ListClientIntegrationsDocument,
        GetIntegrationsSummaryDocument,
      ],
    });

  const { repositoriesOptions, spaceOptions } = useAssignSpacesToRepos({
    scope,
    repositories,
  });

  const pairedSpacesNames = spaceOptions
    .filter((space) =>
      watchAssignedSpaces.find(
        (assignedSpace) => assignedSpace.spaceMrn === space.value,
      ),
    )
    .map((space) => space.label);

  const handleCreateIntegration = async ({
    integrationName,
  }: {
    integrationName: string;
  }) => {
    const configurationOptions: SecurityPipelineGithubConfigurationOptionsInput =
      {};

    try {
      const { data: createIntegrationData } = await createIntegration({
        variables: {
          input: {
            configurationOptions: {
              securityPipelineGithubConfigurationOptions: configurationOptions,
            },
            longLivedToken: false,
            name: integrationName,
            spaceMrn: scope.mrn,
            type: ClientIntegrationType.SecurityPipelineGithub,
          },
        },
      });

      const integrationId =
        createIntegrationData?.createClientIntegration.integration.mrn
          .split("/")
          .pop();
      navigate(
        `/organization/integrations/security_pipeline_github/${integrationId}/overview?${scope.params}`,
      );

      // setStep(1);
    } catch (error) {
      const msg = getError(error);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const handleChangePairing = () => {
    dialogs.open(GithubSecurityPipelineAssignSpacesDialog, {
      control: formMethods.control,
      reset,
      scope,
      repositoriesOptions,
      spaceOptions,
      triggerSubmit: triggerSubmit,
    });
  };

  const handleResetSecurityPipeline = async () => {
    if (!integrationMrn) return;

    try {
      await deleteIntegration({
        variables: {
          input: {
            mrn: integrationMrn,
          },
        },
      });

      enqueueSnackbar("Security pipeline reset successfully!", {
        variant: "success",
      });
    } catch (error) {
      const msg = getError(error);
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      handleCloseConfirmationDialog();
    }
  };

  const handleOpenConfirmationDialog = () => {
    setIsConfirmationDialogOpen(true);
  };

  const handleCloseConfirmationDialog = () => {
    setIsConfirmationDialogOpen(false);
  };

  useEffect(() => {
    if (repositories.length > 0) {
      const configOptions = integration?.configurationOptions;

      if (
        configOptions?.__typename !==
        "SecurityPipelineGithubConfigurationOptions"
      )
        return;

      const reposToRender = repositories
        .filter((repo) => repo.spaceMrn)
        .map((repo) => ({
          spaceMrn: repo.spaceMrn || "",
          repositoryLink: repo.link,
        }));

      reset({
        integrationName: integration?.name,
        assignedSpaces: reposToRender.length > 0 ? reposToRender : [],
        generateSSHKey: !!configOptions?.publicKey,
      });
    }
  }, [repositories]);

  useEffect(() => {
    if (canCreateIntegration) {
      handleCreateIntegration({ integrationName: defaultIntegrationName });
    }
  }, [canCreateIntegration]);

  useEffect(() => {
    if (isSubmitSuccessful && !integration) {
      reset(defaultValues);
    }
  }, [isSubmitSuccessful]);

  const onSubmit: SubmitHandler<GithubSecurityPipelineFormInput> = async (
    data,
  ) => {
    const integrationName =
      data.integrationName?.trim() ||
      integration?.name ||
      `Github ${SECURITY_PIPELINE_DISPLAY_NAME}`;
    const assignedSpaces = data.assignedSpaces;

    try {
      if (integration) {
        if (
          integration.configurationOptions?.__typename !==
          "SecurityPipelineGithubConfigurationOptions"
        )
          return;
        const securityPipelineGithubConfigurationOptions: SecurityPipelineGithubConfigurationOptionsInput =
          {
            repositories: (integration.configurationOptions?.repositories || [])
              .map((repo) => {
                const assignedRepo = assignedSpaces.find(
                  (space) => space.repositoryLink === repo.link,
                );

                if (!assignedRepo)
                  return {
                    ...repo,
                    spaceMrn: "",
                  };

                return {
                  fullName: repo.fullName,
                  spaceMrn:
                    assignedSpaces.find(
                      (space) => space.repositoryLink === repo.link,
                    )?.spaceMrn || "",
                };
              })
              .filter((repo) => repo.spaceMrn),
          };

        await updateIntegration({
          variables: {
            input: {
              name: integrationName,
              mrn: getIntegrationMrn({
                scope,
                integrationId: integration.mrn.split("/").pop() || "",
              }),
              type: ClientIntegrationType.SecurityPipelineGithub,
              configurationOptions: {
                securityPipelineGithubConfigurationOptions,
              },
            },
          },
        });
        enqueueSnackbar("Successfully updated configuration", {
          variant: "success",
        });
        navigate(
          `/organization/integrations/security_pipeline_github/${integrationId}/overview?${scope.params}`,
        );
      } else {
        const securityPipelineGithubConfigurationOptions: SecurityPipelineGithubConfigurationOptionsInput =
          {
            repositories: [],
            generateSSHKey: data.generateSSHKey,
          };

        const { data: createIntegrationData } = await createIntegration({
          variables: {
            input: {
              spaceMrn: scope.mrn,
              name: integrationName,
              type: ClientIntegrationType.SecurityPipelineGithub,
              longLivedToken: false,
              configurationOptions: {
                securityPipelineGithubConfigurationOptions,
              },
            },
          },
        });

        enqueueSnackbar("Successfully created integration", {
          variant: "success",
        });
        navigate(
          `/organization/integrations/security_pipeline_github?${scope.params}`,
        );
      }
    } catch (e) {
      const msg = getError(e);
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const triggerSubmit = () => {
    return formMethods.handleSubmit(onSubmit)();
  };

  return {
    canCreateIntegration,
    isLoading:
      createIntegrationLoading ||
      clientIntegrationLoading ||
      updateIntegrationLoading ||
      deleteIntegrationLoading,
    appInstallationLink,
    repositories,
    pairedSpacesNames,
    publicKey,
    formMethods,
    onSubmit,
    watchRepoName,
    onChangePairing: handleChangePairing,
    isConfirmationDialogOpen,
    handleOpenConfirmationDialog,
    handleCloseConfirmationDialog,
    handleResetSecurityPipeline,
  };
};
